import { NAV_GROUPS, SECTION_INHERITS } from '@/routes/paths';
import type { CurrentUser } from '@/shared/types/auth';
import {
  WORKSPACE_ACCESS_CATALOG,
  type WorkspaceAccessGroup,
  type WorkspaceAccessKey,
} from './workspaceAccess';

/**
 * Per-screen ("section") access, derived from the routing table.
 *
 * A section IS a route: its identity is the nav item's path, so the checkbox an
 * administrator ticks and the URL the user visits cannot drift apart. Adding a
 * nav item to NAV_GROUPS automatically produces a new section — nothing here is
 * a hand-maintained list of screen names.
 *
 * SCOPE OF ENFORCEMENT, stated plainly:
 *  - Sections NARROW a module the user already holds. A section grant without
 *    its module is inert, because the module gate runs first (both in the
 *    sidebar and in auth.js on the server).
 *  - Sections are enforced in the SPA: the sidebar link is hidden and the route
 *    redirects. They are persisted and bounded server-side, but the API
 *    authorization boundary is still the MODULE (workspace-access.js
 *    API_MODULE_PREFIXES) plus each route's own permission guard. Unticking a
 *    section does not by itself revoke the underlying endpoints.
 */

/** Marker proving an admin actually configured sections, so an empty list means "none" rather than "legacy user". */
export const WORKSPACE_SECTIONS_PROFILE = 'workspace.sections.configured';

export const SECTION_PERMISSION_PREFIX = 'workspace.section.';

export interface ModuleSection {
  /** Route path. The stable identity of the screen. */
  path: string;
  label: string;
  module: WorkspaceAccessKey;
  /** Sidebar section the screen appears under. */
  group: WorkspaceAccessGroup;
}

const MODULE_GROUPS = new Map<string, WorkspaceAccessGroup>(
  WORKSPACE_ACCESS_CATALOG.map((module) => [module.key, module.group]),
);

/** Mirrors SECTION_SLUG_PATTERN in workspace-access.js — the shape the server will persist. */
const SECTION_SLUG_PATTERN = /^[a-z0-9]+(?:[.-][a-z0-9]+)*$/;

/**
 * Whether a nav path can be a grantable screen.
 *
 * Excludes parametrised routes: NAV_GROUPS contains two entries pointing at
 * `/manufacturing/batch-ops/:id` and `/manufacturing/costing/:id`, which are not
 * concrete destinations. Offering them as checkboxes would mint a permission the
 * server refuses, so the grant would silently vanish on save.
 *
 * Validating against the server's own pattern makes the contract structural
 * rather than something two files have to remember to agree on.
 */
function isGrantableRoute(path: string): boolean {
  if (!path.startsWith('/') || path.includes(':') || path.includes('*')) return false;
  return SECTION_SLUG_PATTERN.test(path.replace(/^\//, '').replace(/\//g, '.'));
}

/**
 * Every gateable screen, in sidebar order.
 *
 * Only nav items carrying an `access` key are included: an item with no module
 * gate is not section-managed, and pretending otherwise would offer a checkbox
 * that controls nothing.
 */
export const MODULE_SECTIONS: ModuleSection[] = (() => {
  const seen = new Set<string>();
  const sections: ModuleSection[] = [];
  for (const navGroup of NAV_GROUPS) {
    for (const item of navGroup.items) {
      // A nav item may be gated on several modules ("any of these grants it").
      // A section belongs to exactly one module in this matrix, so the FIRST
      // key wins — that is the group the screen is actually filed under in the
      // sidebar (Item Master is access: ['dispatch', 'accounts'], and it lives
      // under Dispatch). The extra keys still widen who can OPEN the screen;
      // they just do not give it a second home in the section manager.
      const moduleKey = Array.isArray(item.access) ? item.access[0] : item.access;
      if (!moduleKey || seen.has(item.path) || !isGrantableRoute(item.path)) continue;
      const group = MODULE_GROUPS.get(moduleKey);
      if (!group) continue;
      seen.add(item.path);
      sections.push({ path: item.path, label: item.label, module: moduleKey, group });
    }
  }
  return sections;
})();

/** Gateable screens for one module, in sidebar order. Empty when the module has no nav entry. */
export function sectionsForModule(module: WorkspaceAccessKey): ModuleSection[] {
  return MODULE_SECTIONS.filter((section) => section.module === module);
}

export function sectionsForModules(modules: readonly WorkspaceAccessKey[]): ModuleSection[] {
  return MODULE_SECTIONS.filter((section) => modules.includes(section.module));
}

/**
 * `/accounting-v2/coa` -> `workspace.section.accounting-v2.coa`
 *
 * Path separators become dots so the string stays a readable, dot-delimited
 * permission like every other one. Safe because no route segment contains a
 * dot — asserted in moduleSections.test.ts.
 */
export function sectionPermission(path: string): string {
  return `${SECTION_PERMISSION_PREFIX}${path.replace(/^\//, '').replace(/\//g, '.')}`;
}

/** Inverse of sectionPermission. Returns null for anything that is not a section grant. */
export function sectionPathFromPermission(permission: string): string | null {
  if (!permission.startsWith(SECTION_PERMISSION_PREFIX)) return null;
  const slug = permission.slice(SECTION_PERMISSION_PREFIX.length);
  if (!slug) return null;
  return `/${slug.replace(/\./g, '/')}`;
}

/** Permission strings for a set of granted screens, including the configured marker. */
export function permissionsForSections(paths: readonly string[]): string[] {
  return [WORKSPACE_SECTIONS_PROFILE, ...paths.map(sectionPermission)];
}

/**
 * Screens this user may open, or `null` when unrestricted.
 *
 * `null` is the backwards-compatible answer for superadmins and for every user
 * created before section grants existed: absent marker means "module access
 * decides", exactly as before.
 */
export function grantedSections(user?: CurrentUser | null): string[] | null {
  if (!user || user.role === 'superadmin') return null;
  const permissions = user.permissions ?? [];
  if (!permissions.includes(WORKSPACE_SECTIONS_PROFILE)) return null;
  return permissions
    .map(sectionPathFromPermission)
    .filter((path): path is string => path !== null);
}

/**
 * The registered section a URL belongs to, by longest matching prefix, so detail
 * routes (`/accounting-v2/items/42`) resolve to their list screen
 * (`/accounting-v2/items`) instead of escaping the gate.
 */
export function sectionForPath(pathname: string): ModuleSection | null {
  let match: ModuleSection | null = null;
  for (const section of MODULE_SECTIONS) {
    const isMatch = pathname === section.path || pathname.startsWith(`${section.path}/`);
    if (isMatch && (!match || section.path.length > match.path.length)) match = section;
  }
  return match;
}

/**
 * Whether a URL is reachable. Unregistered paths always pass: sections narrow
 * the nav, they are not a deny-by-default router.
 */
export function isSectionVisible(granted: readonly string[] | null | undefined, pathname: string): boolean {
  if (!granted) return true;
  const section = sectionForPath(pathname);
  if (!section) return true;
  const parent = SECTION_INHERITS[section.path];
  return granted.includes(section.path) || (parent ? granted.includes(parent) : false);
}
